<script lang="ts">
/** One step in the bar. */
export interface GaiaStepBarItem {
  key: string;
  label: string;
  /** Reachable — a step ahead of the furthest one reached stays inert. */
  enabled?: boolean;
}
</script>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaText from './GaiaText.vue';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  items: {
    type: Array as PropType<GaiaStepBarItem[]>,
    required: true,
  },
  /** Key of the step being edited. */
  active: {
    type: String,
    required: true,
  },
});

const emit = defineEmits<{ select: [key: string] }>();

const activeIndex = computed(() => props.items.findIndex(item => item.key === props.active));

/** Behind the active step = done, and done steps show a check instead of a number. */
function isDone(index: number): boolean {
  return index < activeIndex.value;
}

function pick(item: GaiaStepBarItem, index: number): void {
  if (item.enabled ?? (isDone(index) || item.key === props.active)) emit('select', item.key);
}

const ink = gaiaNeutral.ink;
const onInk = gaiaNeutral.surface;
const border = gaiaNeutral.border;
const idle = gaiaNeutral.textFaint;
const hover = gaiaNeutral.surfaceHover;
// The circle's interior is selector-bound (.done / .current flip its colours),
// so its type reaches it through v-bind rather than a GaiaText prop.
const numSize = gaiaFontSize.nano;
const bold = String(gaiaFontWeight.bold);
</script>

<template>
  <!-- Horizontal numbered stepper for a wizard that keeps its steps in the page
       header (CM view create). GaiaStepRail is the vertical sibling for wizards
       with a left rail. naive's n-steps draws its own chrome and can't express
       the ink circle + check + hairline tail, so the row is ours. -->
  <nav class="gaia-step-bar">
    <template v-for="(item, index) in props.items" :key="item.key">
      <button
        type="button"
        class="step"
        :disabled="!(item.enabled ?? (isDone(index) || item.key === props.active))"
        @click="pick(item, index)"
      >
        <span class="circle" :class="{ done: isDone(index), current: item.key === props.active }">
          <svg
            v-if="isDone(index)"
            width="9"
            height="9"
            viewBox="0 0 10 10"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M1.8 5.2 4 7.4 8.2 2.8" />
          </svg>
          <template v-else>{{ index + 1 }}</template>
        </span>
        <gaia-text
          size="dense"
          :weight="item.key === props.active ? 'bold' : 'medium'"
          :tone="item.key === props.active ? 'ink' : isDone(index) ? 'text' : 'faint'"
        >
          {{ item.label }}
        </gaia-text>
      </button>
      <span v-if="index < props.items.length - 1" class="tail" />
    </template>
  </nav>
</template>

<style scoped>
.gaia-step-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}

.step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  background: none;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}

.step:disabled {
  cursor: default;
}

.step:not(:disabled):hover {
  background: v-bind(hover);
}

/* A fixed circle whose interior font is a box metric, not a type step. */
.circle {
  width: 20px;
  height: 20px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid v-bind(border);
  border-radius: 50%;
  font-size: v-bind(numSize);
  font-weight: v-bind(bold);
  color: v-bind(idle);
}

.circle.done,
.circle.current {
  border-color: v-bind(ink);
  background: v-bind(ink);
  color: v-bind(onInk);
}

.tail {
  width: 18px;
  height: 1px;
  flex: none;
  background: v-bind(border);
}
</style>
